<template>
  <div
    class="pull-to-refresh"
    @touchstart.passive="onTouchStart"
    @touchmove.passive="onTouchMove"
    @touchend="onTouchEnd"
    @touchcancel="onTouchCancel"
  >
    <!-- Pull indicator -->
    <Transition name="ptr-fade">
      <div
        v-if="isPulling || isRefreshing"
        class="pull-to-refresh__indicator"
        :style="{ transform: `translateY(${indicatorOffset}px)` }"
      >
        <div
          class="pull-to-refresh__spinner"
          :class="{ 'pull-to-refresh__spinner--ready': isReady }"
        >
          <svg
            class="pull-to-refresh__icon"
            :class="{ 'pull-to-refresh__icon--spin': isRefreshing }"
            :style="!isRefreshing ? { transform: `rotate(${iconRotation}deg)`, opacity: iconOpacity } : {}"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
        </div>
      </div>
    </Transition>

    <!-- Content with pull offset -->
    <div
      class="pull-to-refresh__content"
      :style="contentStyle"
    >
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'

const props = defineProps<{ disabled?: boolean }>()

const THRESHOLD = 72
const MAX_PULL = 120
const RESISTANCE = 0.4

const startY = ref(0)
const isPulling = ref(false)
const isRefreshing = ref(false)
const isAnimating = ref(false)
const pullDistance = ref(0)

const isReady = computed(() => pullDistance.value >= THRESHOLD && !isRefreshing.value)

const contentOffset = computed(() => {
  if (isRefreshing.value) return 52
  return Math.min(pullDistance.value, MAX_PULL)
})

const indicatorOffset = computed(() => {
  return Math.max(-52, contentOffset.value - 52)
})

const iconRotation = computed(() => {
  return (pullDistance.value / THRESHOLD) * 360
})

const iconOpacity = computed(() => {
  return Math.min(1, pullDistance.value / (THRESHOLD * 0.4))
})

const contentStyle = computed(() => {
  // Only transform while actively pulling/animating. A persistent transform (or
  // will-change) would make any descendant `position: fixed` resolve against
  // this element instead of the viewport (breaks the fixed level rail).
  const active = isPulling.value || isRefreshing.value || isAnimating.value
  if (!active) return {}
  return {
    transform: `translateY(${contentOffset.value}px)`,
    transition: isAnimating.value ? 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
    willChange: 'transform',
  }
})

function onTouchStart(e: TouchEvent) {
  if (props.disabled || isRefreshing.value) return
  if (window.scrollY > 0) return

  startY.value = e.touches[0].clientY
  isPulling.value = true
  isAnimating.value = false
}

function onTouchMove(e: TouchEvent) {
  if (!isPulling.value || isRefreshing.value) return

  const delta = e.touches[0].clientY - startY.value

  if (delta <= 0) {
    pullDistance.value = 0
    return
  }

  pullDistance.value = delta * RESISTANCE
}

/**
 * Chrome на iPhone (и Safari при своём резиновом overscroll) перехватывает
 * жест и шлёт touchcancel вместо touchend: без сброса контент оставался
 * сдвинутым вниз на pullDistance, и верх страницы уезжал под шапку.
 */
function onTouchCancel() {
  if (!isPulling.value) return
  isPulling.value = false
  isAnimating.value = true
  pullDistance.value = 0
  setTimeout(() => {
    isAnimating.value = false
  }, 300)
}

function onTouchEnd() {
  if (!isPulling.value) return
  isPulling.value = false
  isAnimating.value = true

  if (pullDistance.value >= THRESHOLD) {
    isRefreshing.value = true
    pullDistance.value = 0
    setTimeout(() => window.location.reload(), 300)
  } else {
    pullDistance.value = 0
    setTimeout(() => {
      isAnimating.value = false
    }, 300)
  }
}
</script>

<style scoped lang="scss">
.pull-to-refresh {
  position: relative;

  &__indicator {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 999;
    pointer-events: none;
  }

  &__spinner {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: var(--hm-surface);
    box-shadow: 0 2px 12px var(--ds-scrim);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s, box-shadow 0.2s;

    &--ready {
      background: var(--hm-accent);
      box-shadow: 0 2px 16px var(--ds-accent-soft-35);
    }
  }

  &__icon {
    color: var(--hm-accent);
    transition: color 0.2s;

    &--spin {
      animation: ptr-spin 0.7s linear infinite;
    }
  }

  &__spinner--ready &__icon {
    color: var(--hm-text-primary);
  }

  &__content {
    // will-change is applied dynamically only while pulling (see contentStyle)
    // so it never creates a containing block for fixed-position descendants.
  }
}

.ptr-fade-enter-active,
.ptr-fade-leave-active {
  transition: opacity 0.2s ease;
}

.ptr-fade-enter-from,
.ptr-fade-leave-to {
  opacity: 0;
}

@keyframes ptr-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
